import { useDeferredValue, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, BookOpen, LoaderCircle, Search, Sparkles, Star, X } from "lucide-react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { WordSense } from "@preptrack/contracts";
import { Button, EmptyState, LoadingLine, Tag } from "@/components/primitives";
import { HighlightedHeadword } from "@/components/highlighted-headword";
import { WordSenses } from "@/components/word-senses";
import { WordPronunciation } from "@/components/word-pronunciation";
import { apiFetch } from "@/lib/api";

interface Suggestion {
  id: string;
  headword: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  senses: WordSense[];
}
interface WordData {
  id: string;
  headword: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  favorite: boolean;
  senses: WordSense[];
  bookSenses: Array<{ bookId: string; code: string; label: string; senses: WordSense[] }>;
  memberships: Array<{ bookId: string; code: string; label: string; kind: string }>;
  examOccurrences: Array<{ sentenceId: string; sentence: string; year: number; examCode: string; examType: string; section: string; label: string | null }>;
  examples: Array<{ id: string; sentence: string; translation: string | null; sourceType: string }>;
}

export function SearchPage() {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const deferredQuery = useDeferredValue(query.trim());
  const result = useQuery({
    queryKey: ["suggest", deferredQuery],
    queryFn: () => apiFetch<{ suggestions: Suggestion[] }>(`/words/suggest?q=${encodeURIComponent(deferredQuery)}`),
    enabled: deferredQuery.length > 0,
    staleTime: 30_000,
  });
  const suggestions = result.data?.suggestions ?? [];
  const panelOpen = isOpen && query.trim().length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [deferredQuery, suggestions.length]);

  useEffect(() => {
    const focusSearch = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        if (query.trim()) setIsOpen(true);
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, [query]);

  const openSuggestion = (suggestion: Suggestion) => {
    setIsOpen(false);
    void navigate({
      to: "/app/$subject/words/$word",
      params: { subject: "english", word: suggestion.id },
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter" && panelOpen) {
      event.preventDefault();
      const selectedSuggestion = suggestions[activeIndex] ?? suggestions[0];
      if (selectedSuggestion) openSuggestion(selectedSuggestion);
    }
  };

  return (
    <div className="page search-page">
      <section className="search-hero">
        <BookOpen className="search-hero__mark" />
        <h1>Fast search.<br /><em>Find words, build vocab.</em></h1>
        <div
          className="search-combobox"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
          }}
        >
          <div className="search-box">
            <Search aria-hidden="true" />
            <input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setIsOpen(true);
              }}
              onFocus={() => {
                if (query.trim()) setIsOpen(true);
              }}
              onKeyDown={handleKeyDown}
              placeholder="输入英文单词"
              aria-label="查询英文单词"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="word-suggestions"
              aria-expanded={panelOpen}
              aria-activedescendant={panelOpen && suggestions[activeIndex] ? `word-suggestion-${suggestions[activeIndex].id}` : undefined}
            />
            {query ? (
              <button
                className="search-box__clear"
                type="button"
                onClick={() => {
                  setQuery("");
                  setIsOpen(false);
                  inputRef.current?.focus();
                }}
                aria-label="清空查询"
              >
                <X />
              </button>
            ) : <kbd>Ctrl K</kbd>}
          </div>
          <div
            id="word-suggestions"
            className={panelOpen ? "suggestion-list suggestion-list--open" : "suggestion-list"}
            role="listbox"
            aria-label="查词建议"
            aria-hidden={!panelOpen}
          >
            {query.trim() && result.isFetching && suggestions.length === 0 ? (
              <div className="suggestion-state suggestion-state--loading" role="status">
                <LoaderCircle />
                <span>正在查询词库</span>
              </div>
            ) : null}
            {query.trim() && suggestions.map((item, index) => (
              <div
                key={item.id}
                className={index === activeIndex ? "suggestion suggestion--active" : "suggestion"}
                style={{ animationDelay: `${index * 28}ms` }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <Link
                  id={`word-suggestion-${item.id}`}
                  to="/app/$subject/words/$word"
                  params={{ subject: "english", word: item.id }}
                  className="suggestion__main"
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={-1}
                  onFocus={() => setActiveIndex(index)}
                  onClick={() => setIsOpen(false)}
                >
                  <span className="suggestion__word">{item.headword}</span>
                  <WordSenses senses={item.senses} variant="compact" />
                  <ArrowRight className="suggestion__arrow" aria-hidden="true" />
                </Link>
                <WordPronunciation wordId={item.id} headword={item.headword} phoneticUk={item.phoneticUk} phoneticUs={item.phoneticUs} compact />
              </div>
            ))}
            {query.trim() && !result.isFetching && suggestions.length === 0 ? (
              <div className="suggestion-state" role="status">
                <strong>没有找到匹配词条</strong>
                <span>检查拼写，或将它加入阅读生词。</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className={query ? "search-hints search-hints--quiet" : "search-hints"} aria-hidden={Boolean(query)}>
          <span>支持词形与前缀联想</span>
          <span>联查 2000–2026 英语一、英语二真题</span>
        </div>
      </section>
    </div>
  );
}
export function WordPage() {
  const { word: routeWord } = useParams({ from: "/app/$subject/words/$word" });
  const query = useQuery({
    queryKey: ["word", routeWord],
    queryFn: () => apiFetch<{ word: WordData }>(`/words/${encodeURIComponent(routeWord)}`),
  });
  const word = query.data?.word;
  const [favorite, setFavorite] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<{ sentence: string; translation: string } | null>(null);

  useEffect(() => {
    if (word) setFavorite(word.favorite);
  }, [word]);

  if (query.isLoading) return <div className="page"><LoadingLine /></div>;
  if (!word) return <div className="page"><EmptyState title="没有找到这个词" description="返回查词页尝试其他拼写，或把它加入阅读生词。" action={<Link className="button button--primary button--md" to="/app/$subject/search" params={{ subject: "english" }}>返回查词</Link>} /></div>;

  const toggleFavorite = async () => {
    const previous = favorite;
    setFavorite(!previous);
    try {
      const result = await apiFetch<{ favorite: boolean }>(`/words/${word.id}/favorite`, { method: "POST" });
      setFavorite(result.favorite);
    } catch {
      setFavorite(previous);
    }
  };
  const generate = async () => {
    setGenerating(true);
    try {
      const result = await apiFetch<{ example: { sentence: string; translation: string } }>(`/words/${word.id}/generate-example`, { method: "POST" });
      setGenerated(result.example);
    } finally {
      setGenerating(false);
    }
  };
  const shownExample = generated ?? word.examples[0] ?? null;

  return (
    <div className="page word-page">
      <Link to="/app/$subject/search" params={{ subject: "english" }} className="back-link"><ArrowLeft /> 返回查词</Link>
      <header className="word-hero">
        <div className="word-hero__identity">
          <h1>{word.headword}</h1>
          <WordPronunciation wordId={word.id} headword={word.headword} phoneticUk={word.phoneticUk} phoneticUs={word.phoneticUs} />
        </div>
        <button className={favorite ? "favorite-button favorite-button--active" : "favorite-button"} onClick={toggleFavorite} type="button">
          <Star fill={favorite ? "currentColor" : "none"} /> {favorite ? "已收藏" : "收藏"}
        </button>
      </header>

      <section className="word-section definition-list">
        <h2>释义</h2>
        <WordSenses senses={word.senses} empty="这个词条暂时没有词典释义。" />
      </section>

      {word.bookSenses.map((group) => (
        <section className="word-section definition-list" key={group.bookId}>
          <h2>{group.label}释义</h2>
          <WordSenses senses={group.senses} />
        </section>
      ))}

      <section className="word-section">
        <h2>词汇标签</h2>
        <div className="tag-list">
          {word.memberships.length ? word.memberships.map((membership) => <Tag key={membership.code} variant="metadata" tone={membership.kind === "official" ? "accent" : "neutral"}>{membership.label}</Tag>) : <span>暂未归属已发布词库</span>}
        </div>
      </section>

      <section className="word-section exam-example">
        <h2>真题例句</h2>
        <div>
          {word.examOccurrences.length ? word.examOccurrences.map((occurrence) => (
            <blockquote key={occurrence.sentenceId}>
              <p><HighlightedHeadword text={occurrence.sentence} headword={word.headword} /></p>
              <cite>— {occurrence.year} {occurrence.examType} · {occurrence.section} {occurrence.label}</cite>
            </blockquote>
          )) : <p>2000–2026 真题中暂未匹配到该词。</p>}
        </div>
      </section>

      <section className="word-section">
        <h2>更多例句</h2>
        <div className="generated-example">
          {shownExample ? <blockquote><p><HighlightedHeadword text={shownExample.sentence} headword={word.headword} /></p><span>{shownExample.translation}</span></blockquote> : <p>用 MiMo 生成一个贴近考研阅读语境的新例句。</p>}
          <Button onClick={generate} disabled={generating}><Sparkles /> {generating ? "正在生成…" : "生成新例句"}</Button>
        </div>
      </section>
    </div>
  );
}
